import { useState, useCallback } from 'react';
import { explainAnalyzeQuery } from '../../api';
import type { ExplainAnalyzeRequest, ExplainAnalyzeResponse } from './types/database';

interface UseExplainAnalyzeReturn {
  data: ExplainAnalyzeResponse | null;
  isLoading: boolean;
  isError: Error | null;
  execute: (request: ExplainAnalyzeRequest) => Promise<void>;
  reset: () => void;
}

/**
 * Hook to execute EXPLAIN ANALYZE on SQL queries
 *
 * This hook provides a way to analyze query execution plans using PostgreSQL's
 * EXPLAIN ANALYZE functionality. It does not use SWR since each analysis is
 * a one-time operation triggered by user action.
 *
 * @returns Object with data, loading state, error state, execute function, and reset function
 *
 * @example
 * const { data, isLoading, execute } = useExplainAnalyze();
 *
 * const handleAnalyze = async () => {
 *   await execute({
 *     query: "SELECT * FROM users WHERE id = 1",
 *     analyze: true
 *   });
 * };
 */
export function useExplainAnalyze(): UseExplainAnalyzeReturn {
  const [data, setData] = useState<ExplainAnalyzeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState<Error | null>(null);

  const execute = useCallback(async (request: ExplainAnalyzeRequest) => {
    setIsLoading(true);
    setIsError(null);

    try {
      const result = await explainAnalyzeQuery(request);
      setData(result);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to analyze query');
      setIsError(err);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setIsError(null);
    setIsLoading(false);
  }, []);

  return {
    data,
    isLoading,
    isError,
    execute,
    reset,
  };
}
